import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import FolderIcon from "@mui/icons-material/Folder";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import TreeItem, { TreeItemProps } from "@mui/lab/TreeItem";
import TreeView from "@mui/lab/TreeView";
import { Box, IconButton } from "@mui/material";
import Typography from "@mui/material/Typography";
import { IBaseContent, IContentType, IFullFlight } from "@vmosolutions/oc-interfaces";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { openWindow } from "src/redux/actions/Mosaic.Actions";
import { getPreviewViewId, isPreviewable } from "src/utils/contentPreview";
import { convertDateObjectToString } from "src/utils";
import { ISortedContent } from "./DisplayContent";

type TreeItemPropsWithoutContent = Omit<TreeItemProps, "content">;
interface IStyledTreeItemProps extends TreeItemPropsWithoutContent {
  bgColor?: string;
  color?: string;
  labelIcon: any;
  labelInfo?: string;
  labelText: string;
  content?: IBaseContent;
  flightId?: string;
  flight?: IFullFlight;
  path?: string[];
  contentTypes?: IContentType[];
  setLoadedEvent?: any;
  icons?: any;
}

const StyledTreeItem = (props: IStyledTreeItemProps): JSX.Element => {
  const dispatch = useDispatch();
  const {
    labelText,
    labelIcon: LabelIcon,
    labelInfo,
    color,
    bgColor,
    content,
    path,
    flightId,
    flight,
    contentTypes,
    setLoadedEvent,
    icons,
    ...other
  } = props;
  return (
    <TreeItem
      sx={(theme) => ({
        width: "100%",
        color: `${theme.typography.body1.color} !important`,
      })}
      label={
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {LabelIcon && <LabelIcon color="inherit" />}
            <Typography
              component="span"
              sx={(theme) => ({ ml: 0.5, color: theme.palette.common.white })}
              variant="body2"
              onClick={(event: any) => {
                if (content) {
                  dispatch(
                    openWindow(
                      "Edit Content",
                      "ManageContentWindow",
                      [],
                      {
                        initialSelectedFlightId: flightId,
                        contentId: content?._id,
                        setLoadedEvent: setLoadedEvent,
                        path: path,
                      },
                      "context"
                    )
                  );
                }
              }}
            >
              {labelText}
            </Typography>
            <Typography variant="caption" color="inherit">
              {labelInfo}
            </Typography>
          </Box>
          {icons && <Box sx={{ display: "flex", alignItems: "center", selfJustify: "end" }}>{icons()}</Box>}
        </Box>
      }
      {...other}
    />
  );
};

interface IContentTreeProps {
  flight: IFullFlight;
  flightId: string;
  content: ISortedContent;
  path: string[];
  contentTypes: IContentType[];
  setLoadedEvent?: any;
}

/**
 * A component to render the content on a flight in a file tree view
 * @param props - flightId of selected flight, content - the content of the flight sorted, content types & path
 * @returns - JSX file tree
 */
export const ContentTree = (props: IContentTreeProps): JSX.Element => {
  const { content, flightId, path, contentTypes, flight, setLoadedEvent } = props;
  const dispatch = useDispatch();

  const downloadFile = useCallback(
    async (contentId: string, fileType: string, fileName: string) => {
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: "Downloading file...",
        successMessage: "File downloaded.",
        errorMessage: "Error: Unable to download file",
      };
      const response: any = await AmplifyAPIWrapper.Get<any>(`/fileURL/${contentId}/${fileType}`, notifications);
      if (!response?.url) return;
      const downloadLink = document.createElement("a");
      // Add the element to the DOM - to stop the wws disconnect in firefox
      document.body.appendChild(downloadLink);
      downloadLink.href = response.url;
      downloadLink.target = "_blank";
      downloadLink.download = fileName;
      downloadLink.click();
      return response.url;
    },
    [dispatch]
  );

  return (
    <TreeView
      defaultCollapseIcon={<ArrowDropDownIcon />}
      defaultExpandIcon={<ArrowRightIcon />}
      defaultEndIcon={<div style={{ width: 24 }} />}
    >
      {contentTypes?.map((type: IContentType, key: number) => (
        <StyledTreeItem
          nodeId={key.toString() + type._id}
          labelText={type.title}
          labelIcon={FolderIcon}
          key={key.toString() + type._id}
          setLoadedEvent={setLoadedEvent}
        >
          {content[type._id]?.map((each: IBaseContent, key: number) => {
            const icons = (): JSX.Element => {
              const fileName = each?.displayTitle
                ? `${each.contentType.title} - ${each.displayTitle}`
                : each.contentType.title;
              return (
                <>
                  <IconButton
                    sx={{ borderRadius: "3px", p: 0.5, mx: 1 }}
                    onClick={() => {
                      dispatch(
                        openWindow("Edit Content", "ManageContentWindow", [], { contentId: each._id }, "context")
                      );
                    }}
                  >
                    <EditIcon sx={{ height: "20px", width: "20px" }} />
                  </IconButton>
                  {each.hasOwnProperty("fileType") && isPreviewable(each.fileType) && (
                    <IconButton
                      sx={{ borderRadius: "3px", p: 0.5, mr: 0.5 }}
                      onClick={() => {
                        const viewId = getPreviewViewId(each.fileType);
                        if (viewId) {
                          dispatch(openWindow(fileName, viewId, [], { file: each }, "content", true));
                        }
                      }}
                    >
                      <ZoomInIcon sx={{ height: "20px", width: "20px" }} />
                    </IconButton>
                  )}

                  <IconButton
                    sx={{ borderRadius: "3px", p: 0.5 }}
                    onClick={() => {
                      downloadFile(each._id, each.fileType, fileName);
                    }}
                  >
                    <FileDownloadIcon sx={{ height: "20px", width: "20px" }} />
                  </IconButton>
                </>
              );
            };
            return (
              <StyledTreeItem
                nodeId={key.toString() + each._id}
                labelText={
                  each.displayTitle
                    ? each.displayTitle
                    : each?.createdAt
                      ? convertDateObjectToString(new Date(each?.createdAt), false)
                      : each._id
                }
                labelIcon={() => <FilePresentIcon />}
                key={each._id}
                content={each}
                flightId={flightId}
                flight={flight}
                setLoadedEvent={setLoadedEvent}
                path={path}
                contentTypes={contentTypes}
                icons={icons}
              />
            );
          })}
        </StyledTreeItem>
      ))}
    </TreeView>
  );
};
